import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AppContentWrapper } from "../../../../framework/components/AppContentWrapper";
import OrderSearchForm, { OrderSearchFields } from "../components/OrderSearchForm";
import { DropdownOptionProps } from "@abb/abb-common-ux-react";
import {
  Grid,
  getSortModel,
  getDefaultColumnSort
} from "../../../../framework/components/grid/Grid";
import {
  ColDef,
  IGetRowsParams,
  GridApi,
  ValueFormatterParams,
  RowClickedEvent,
  IDatasource,
  RowNode,
  ColumnApi,
  ApplyColumnStateParams
} from "ag-grid-community";

import { getApiRegistry } from "../../../../framework/state/apiRegistry";
import { useSelector, useDispatch } from "react-redux";
import {
  selectOrderCriteria,
  selectOrderSearchFields,
  selectSelectedOrderRowId,
  selectOrderMetaData,
  defaultOrderCriteria
} from "../reducers/ordersViewReducer";
import { ApiOrderGetRequest, OrderDto, AccessRights, PlantDto } from "../../../../api";
import produce from "immer";
import { ordersViewActions } from "../actions/ordersViewActions";
import moment from "moment";
import { getSelectedCompany } from "../../deliveriesHome/reducers/deliveriesHomeViewReducer";
import { orderUrls } from "../orderUrls";
import { mapOrderFieldsToOrderCriteria } from "../../../../utilities/mappings";
import { getValidDateFormat } from "../../../../utilities/dateUtils";
import { ITooltipParams } from "ag-grid-community";
import { isAuthorizedToComponent } from "../../../../utilities/authUtils";
import { getUserAccessRights } from "../../../common/reducers/userReducer";
import { orderSagas } from "../sagas/orderSagas";
import { apiGridErrorHandler } from "../../../../utilities/errorUtils";
import { useTranslation } from "react-i18next";
import { roleConstants } from "constants/roleConstants";
import { TFunction } from "i18next";

export const supplyingPlantField = "supplyingPlant";

export const getOrderViewcolumnDefs = (t: TFunction, showPstReference?: boolean) => {
  const definitions: ColDef[] = [
    {
      field: supplyingPlantField,
      headerTooltip: t("Supplying plant"),
      headerName: t("Supplying plant"),
      tooltipField: "supplyingPlant"
    },
    {
      field: "orderAcknowledgementNumber",
      tooltipField: "orderAcknowledgementNumber",
      headerName: t("Order acknowledgement no."),
      headerTooltip: t("Order acknowledgement no."),
      maxWidth: 250,
      minWidth: 100
    },
    {
      field: "purchaseOrderNumber",
      tooltipField: "purchaseOrderNumber",
      headerName: t("Purchase order no."),
      headerTooltip: t("Purchase order no."),
      flex: 1
    },
    {
      field: "endCustomerPurchaseOrderNumber",
      tooltipField: "endCustomerPurchaseOrderNumber",
      headerName: t("End customer purchase order no."),
      headerTooltip: t("End customer purchase order no.")
    },
    {
      field: "offerReference",
      tooltipField: "offerReference",
      headerName: t("Offer reference"),
      headerTooltip: t("Offer reference"),
      hide: !showPstReference
    },
    {
      field: "created",
      headerName: t("Created date"),
      headerTooltip: t("Created date"),
      minWidth: 120,
      maxWidth: 140,
      valueFormatter: (params: ValueFormatterParams): string => {
        if (!params.value) {
          return "";
        }
        return moment(params.value).format(getValidDateFormat());
      },
      tooltipValueGetter: (params: ITooltipParams): string =>
        moment(params.value).format(getValidDateFormat())
    },
    { field: "productInformation", headerName: t("Product information"), hide: true, width: 150 },
    {
      field: "status",
      headerName: t("Status"),
      headerTooltip: t("Status"),
      minWidth: 120,
      maxWidth: 140,
      valueFormatter: (params: ValueFormatterParams): string => {
        return t(params.value);
      },
      width: 150
    }
  ];

  return definitions;
};

const getCountryOptions = (countryCodes: string[]): DropdownOptionProps[] | undefined => {
  if (countryCodes.length && countryCodes[0] !== null) {
    return countryCodes.map((c) => {
      return { label: c, value: c };
    });
  }
};

const getPlantOptions = (plants: PlantDto[]): DropdownOptionProps[] | undefined => {
  if (plants.length && plants[0] !== null) {
    return plants.map((c) => {
      return { label: c.name, value: c.id.toString() };
    });
  }
};

const OrdersView: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const selectedCompany = useSelector(getSelectedCompany);
  const history = useHistory();
  const columnApi = useRef<ColumnApi | undefined>();

  const plants = useSelector(selectOrderMetaData).plants;
  const storedOrderSearchFields = useSelector(selectOrderSearchFields);
  const criteria = useSelector(selectOrderCriteria);
  const selectedOrderRowId = useSelector(selectSelectedOrderRowId);
  const countryCodes = useSelector(selectOrderMetaData).countryCodes;
  const userAccessRights = useSelector(getUserAccessRights);

  useEffect(() => {
    if (
      isAuthorizedToComponent(userAccessRights, {
        operation: "any",
        accessRights: [AccessRights.ManageDeliveriesCompanies, AccessRights.AdvancedOrderTracking],
        identifier: roleConstants.GlobalRoleIdentifier
      })
    ) {
      dispatch(orderSagas.getOrderMetaData.createAction(undefined));
    }
  }, [userAccessRights, dispatch]);

  const [ordersGridApi, setOrdersGridApi] = useState<GridApi | undefined>(undefined);
  const onSetOrdersGridApiRefSet = useCallback((ref: GridApi | undefined) => {
    setOrdersGridApi(ref);
  }, []);

  const { t } = useTranslation();

  const goToOrderDetails = (orderAcknowledgementNumber: string, gridRowIndex?: number | null) => {
    dispatch(ordersViewActions.storeClickedGridIndex(gridRowIndex ?? undefined));
    history.push(orderUrls.orderDetails(orderAcknowledgementNumber));
  };

  const hasOrderTrackingRights = isAuthorizedToComponent(userAccessRights, {
    operation: "any",
    identifier: selectedCompany?.value ?? "0",
    accessRights: [AccessRights.AdvancedOrderTracking, AccessRights.ManageDeliveriesCompanies]
  });

  const dataSource = (
    orderCriteria: ApiOrderGetRequest,
    selectedOrderRowId: number | undefined
  ): IDatasource => {
    let hasFoundNode = false;
    let nodeToSelect: RowNode | null | undefined = null;
    return {
      getRows: (params: IGetRowsParams) => {
        const page = Math.floor(params.startRow / criteria.criteriaPageSize);
        const api = getApiRegistry().orderApi;

        const sortModel = getSortModel(params.sortModel);

        const newCriteria = produce(orderCriteria, (draft) => {
          draft.criteriaSortColumn = sortModel?.sortColumn;
          draft.criteriaPage = page;
          draft.criteriaIsAscendingOrder = sortModel?.isAsc;
        });

        dispatch(ordersViewActions.changeOrderCriteria(newCriteria));
        ordersGridApi?.showLoadingOverlay();

        api
          .apiOrderGet(newCriteria)
          .then((res) => {
            if (res.criteria.totalCount === 1 && selectedOrderRowId === undefined) {
              goToOrderDetails(res.orders[0].identifier, 0);
            }

            if (res.orders.length === 0) {
              ordersGridApi?.showNoRowsOverlay();
            } else {
              ordersGridApi?.hideOverlay();
            }

            params.successCallback(res.orders, res.criteria.totalCount);

            if (selectedOrderRowId !== undefined && !hasFoundNode) {
              nodeToSelect = ordersGridApi?.getDisplayedRowAtIndex(selectedOrderRowId);
              hasFoundNode = true;
              dispatch(ordersViewActions.storeClickedGridIndex(undefined));
            }

            if (nodeToSelect && selectedOrderRowId != null) {
              ordersGridApi?.ensureIndexVisible(selectedOrderRowId, "middle");
              nodeToSelect.setSelected(true, true);
              const selectedRows = ordersGridApi?.getSelectedRows();
              if (selectedRows && selectedRows.length > 0) {
                // Row is selected from the grid and we don't want to override manual selection
                nodeToSelect = null;
              }
            }

            //ordersGridApi?.sizeColumnsToFit();
          })
          .catch((err) => {
            ordersGridApi?.hideOverlay();
            apiGridErrorHandler(err, dispatch);
          });
      }
    };
  };

  // Plant is not visible if user lacks order tracking rights.
  // Therefore, reset sort if changing to a company, to which user does not have order tracking access right.
  const handlePossibleSortReset = () => {
    const gridSortModel = columnApi.current?.getColumnState();
    const sortModel = gridSortModel ? getSortModel(gridSortModel) : undefined;
    if (
      !hasOrderTrackingRights &&
      sortModel !== undefined &&
      sortModel.sortColumn === supplyingPlantField &&
      defaultOrderCriteria.criteriaSortColumn
    ) {
      columnApi.current?.applyColumnState({
        state: [
          {
            colId: defaultOrderCriteria.criteriaSortColumn,
            sort: defaultOrderCriteria.criteriaIsAscendingOrder ? "asc" : "desc"
          }
        ]
      });
    }
  };

  return (
    <AppContentWrapper
      sideBar={
        <OrderSearchForm
          countries={getCountryOptions(countryCodes)}
          plants={getPlantOptions(plants)}
          gridApi={ordersGridApi}
          onSubmit={(values: OrderSearchFields, companyId?: string | null) => {
            dispatch(ordersViewActions.changeSearchFields(values));
            handlePossibleSortReset();
            ordersGridApi?.setDatasource(
              dataSource(
                mapOrderFieldsToOrderCriteria(
                  !companyId ? { ...values } : { ...values, countryCode: undefined },
                  criteria,
                  companyId
                ),
                selectedOrderRowId
              )
            );
          }}
          selectedCompany={selectedCompany}
          storedOrderFields={storedOrderSearchFields}
        />
      }
      sideBarTitle={t("Search form")}
      pageTitle={t("Orders")}
    >
      <Grid
        isSortPersisted={true}
        defaultSort={getDefaultColumnSort(
          defaultOrderCriteria.criteriaIsAscendingOrder,
          defaultOrderCriteria.criteriaSortColumn
        )}
        isOnlyResetSorting={true}
        showStatusBar={{ showResetButton: true, showRowCount: true }}
        columnDefs={getOrderViewcolumnDefs(t).map((colDef) => {
          if (colDef.field === "supplyingPlant") {
            return {
              ...colDef,
              hide: !hasOrderTrackingRights || storedOrderSearchFields.limitedSearch
            };
          }
          return colDef;
        })}
        rowModelType={"infinite"}
        cacheBlockSize={100}
        maxBlocksInCache={2}
        infiniteInitialRowCount={0}
        autosizeCols
        disableColumnAutoSize
        onRowClicked={(event: RowClickedEvent) => {
          const order = event.data as OrderDto;
          goToOrderDetails(order.identifier, event.rowIndex);
        }}
        showPointerOnRowHover
        onGridReady={(event) => {
          columnApi.current = event.columnApi;
          onSetOrdersGridApiRefSet(event.api);
          const sortModelToSet: ApplyColumnStateParams = {
            state: criteria.criteriaSortColumn
              ? [
                  {
                    colId: criteria.criteriaSortColumn,
                    sort: criteria.criteriaIsAscendingOrder ? "asc" : "desc"
                  }
                ]
              : undefined
          };
          event.columnApi.applyColumnState(sortModelToSet);
        }}
        t={t}
      />
    </AppContentWrapper>
  );
};

export default OrdersView;
